import {routes} from '@utilities/constants/routes.tsx'
import {generatePath, matchPath, useLocation} from 'react-router-dom'
import {FC, useMemo} from 'react'
import {useTranslation} from '@/translations/i18n.tsx'
import {CalendarIcon, MedicalCrossIcon, PrinterIcon, RefreshCw05Icon, User01Icon} from '@components/ui/icon/Icon.tsx'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {PatientExtended} from '@/features/patients/types'
import {Tab} from '@/components/ui/tab-atoms/TabAtoms'
import {StyledGenerationHelpText, StyledPatientDetailsTabs, StyledPrintCoverButton} from './style'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {useTheme} from 'styled-components'
import {Text} from '@/components/ui/text/Text'
import {Button} from '@/components/ui/button/Button'
import {useGenerateMedicalRecordCover} from '@/features/medical-records/queries/useGenerateMedicalRecordCover'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {useGetMedicalRecord} from '@/features/medical-records/queries/useGetMedicalRecord'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {usePermissions} from '@/features/auth/store/store'
import {hasPassed5Minutes} from '@/features/medical-records/utils'

export const PatientDetailsTab: FC<{patient: PatientExtended}> = ({patient}) => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const location = useLocation()
    const {palette} = useTheme()
    const {data: medicalRecord, isPending: isLoadingMedicalRecord} = useGetMedicalRecord(
        {medicalRecordId: patient.medicalRecord?.id},
        {
            enabled: !!patient.medicalRecord?.id,
            refetchInterval: query => {
                const data = query.state.data
                if (!data?.pdfGenerationStartedAt) {
                    return false // Don't poll if there's no generation yet
                }

                // Stop polling if generation is completed
                if (data.pdfGenerationCompletedAt) {
                    return false
                }

                // Stop polling if more than 5 minutes have passed since generation started
                if (hasPassed5Minutes(data.pdfGenerationStartedAt)) {
                    return false
                }

                // Poll every 5 seconds while generation is in progress
                return 5000
            }
        }
    )
    const {mutate: generateMedicalRecordCover, isPending: isGeneratingMedicalRecordCover} =
        useGenerateMedicalRecordCover(
            {medicalRecordId: patient.medicalRecord?.id},
            {
                onSuccess: async () => {
                    await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENTS, patient.id]})
                    await queryClient.invalidateQueries({
                        queryKey: [QUERY_KEYS.MEDICAL_RECORD, {medicalRecordId: patient.medicalRecord?.id}]
                    })
                }
            }
        )

    const canRegenerateCover = useMemo(() => {
        if (!medicalRecord?.pdfGenerationStartedAt) {
            return true
        }

        return hasPassed5Minutes(medicalRecord.pdfGenerationStartedAt)
    }, [medicalRecord])

    return (
        <Flexbox fullWidth style={{height: '100%'}} justify="space-between" align="center">
            <StyledPatientDetailsTabs paddingX={4}>
                <Flexbox fullWidth>
                    <Tab to={generatePath(routes.PATIENT_DETAILS_INFORMATION.handle.path, {id: patient.id})}>
                        <Flexbox align="center" gap={1}>
                            <User01Icon />
                            {t('users:information')}
                        </Flexbox>
                    </Tab>
                    {permissions?.medicalRecords.read && (
                        <Tab to={generatePath(routes.PATIENT_DETAILS_MEDICAL_RECORDS.handle.path, {id: patient.id})}>
                            <Flexbox align="center" gap={1}>
                                <MedicalCrossIcon />
                                {t('commons:medical_record', {
                                    code: patient.medicalRecord?.code ? `n°${patient.medicalRecord.code}` : ''
                                })}
                            </Flexbox>
                        </Tab>
                    )}
                    <Tab to={generatePath(routes.PATIENT_DETAILS_EVENTS.handle.path, {id: patient.id})}>
                        <Flexbox align="center" gap={1}>
                            <CalendarIcon />
                            {t('patients:visits_tab_title')}
                        </Flexbox>
                    </Tab>
                </Flexbox>
                {permissions?.medicalRecords.write &&
                    patient.medicalRecord &&
                    matchPath(
                        {
                            path: routes.PATIENT_DETAILS_MEDICAL_RECORDS.handle.path
                        },
                        location.pathname
                    ) &&
                    (isLoadingMedicalRecord ? (
                        <Flexbox align="center" gap={2} fullWidth justify="end">
                            <Skeleton height={20} width={120} />
                        </Flexbox>
                    ) : medicalRecord && medicalRecord.coverAttachment ? (
                        <Tooltip
                            isOpenOnClickEnabled={false}
                            contentProps={{side: 'top'}}
                            variant="light"
                            content={t('medical_records:print_cover')}
                            triggerProps={{asChild: true}}
                        >
                            <StyledPrintCoverButton
                                variant="tertiary"
                                shape="square"
                                to={medicalRecord.coverAttachment?.url}
                                target="_blank"
                            >
                                <PrinterIcon />
                            </StyledPrintCoverButton>
                        </Tooltip>
                    ) : canRegenerateCover && !isGeneratingMedicalRecordCover ? (
                        <Flexbox align="center" gap={2} fullWidth justify="end">
                            <Button variant="ghost" onClick={() => generateMedicalRecordCover()}>
                                <Text size="textXs" palette="neutral" shade="500" weight={400}>
                                    {t('patients:generate_medical_record_cover')}
                                </Text>
                                <RefreshCw05Icon color={palette.primary['500']} />
                            </Button>
                        </Flexbox>
                    ) : (
                        <StyledGenerationHelpText align="center" gap={2}>
                            <Text size="textXs" palette="neutral" shade="500" weight={400}>
                                {t('patients:creating_medical_record_cover')}
                            </Text>
                            <Spinner size={15} color={palette.primary['500']} />
                        </StyledGenerationHelpText>
                    ))}
            </StyledPatientDetailsTabs>
        </Flexbox>
    )
}
